/**
 * Admission / General Consent — A4 portrait, bilingual (English + Hindi).
 *
 * Pre-fills admission information (patient, UHID, sex, age, guardian, address,
 * IPD No., doctor, ward, bed, admission date/time, payer details) and leaves
 * signature blocks for handwritten capture.
 *
 * Route: /print/ipd-consent/:admissionId
 * Auto-prints on load (append ?autoprint=0 to suppress for preview).
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, BRAND } from "@/lib/api";
import PrintHeader from "@/components/PrintHeader";
import LetterheadToggle, { useLetterheadMode } from "@/components/LetterheadToggle";
import { useUserNames } from "@/lib/useUserNames";

const dot = (cap = 40) => ".".repeat(cap);

function Filled({ value, cap = 40 }) {
  const text = (value ?? "").toString().trim();
  if (text) {
    return <span className="font-semibold underline decoration-dotted underline-offset-4">{text}</span>;
  }
  return <span className="text-ink-muted">{dot(cap)}</span>;
}

function fmtIstDateTime(iso) {
  if (!iso) return { date: "", time: "" };
  // iso may already carry +05:30; slice cleanly
  const [d, t] = iso.split("T");
  if (!t) return { date: d || "", time: "" };
  return { date: d, time: t.slice(0, 5) };
}

export default function PrintAdmissionConsent() {
  const { admissionId } = useParams();
  const [adm, setAdm] = useState(null);
  const [patient, setPatient] = useState(null);
  const [hospital, setHospital] = useState(null);
  const [branch, setBranch] = useState(null);
  const [doctorName, setDoctorName] = useState("");
  const letterhead = useLetterheadMode();
  const nameOf = useUserNames();

  useEffect(() => {
    (async () => {
      try {
        const me = await api.get("/auth/me");
        setHospital(me.data.hospital);
        setBranch(me.data.branch);
        const { data } = await api.get(`/ipd/admissions/${admissionId}`);
        const a = data?.admission || data;
        setAdm(a);
        if (a?.patient_id) {
          const p = await api.get(`/patients/${a.patient_id}`);
          setPatient(p.data.patient);
        }
        if (a?.admitting_doctor_id) {
          try {
            const doc = await api.get("/hospital/doctors");
            const d = (doc.data.items || []).find((x) => x.id === a.admitting_doctor_id);
            if (d) setDoctorName(d.name || "");
          } catch { /* non-fatal */ }
        }
        const auto = new URLSearchParams(window.location.search).get("autoprint");
        if (auto !== "0") setTimeout(() => window.print(), 800);
      } catch (e) {
        // surface a friendly message in the page if anything fails
        console.error("Consent load failed", e);
      }
    })();
  }, [admissionId]);

  if (!adm || !patient) return <div className="p-10 text-sm text-ink-muted">Loading consent form…</div>;

  const dt = fmtIstDateTime(adm.admitted_at);
  const guardian = (patient.guardian || "").trim();
  const address = (patient.address || "").trim();

  return (
    <>
      <style>{`
        @page { size: A4; margin: 12mm 14mm; }
        body { background: white; }
        @media print {
          .no-print { display: none !important; }
        }
      `}</style>
      <div className="mx-auto bg-white text-[12.5px] leading-relaxed text-ink" style={{ width: "190mm", padding: "0", boxSizing: "border-box" }} data-testid="print-admission-consent">

        {/* Hospital header */}
        <LetterheadToggle />
        {!letterhead && (
          <PrintHeader
            compact
            hospital={hospital}
            branch={branch}
            subtitle="Consent for Admission & Treatment · प्रवेश एवं उपचार के लिए सहमति"
            right={
              <>
                <div>Date / दिनांक: <b>{dt.date || new Date().toISOString().slice(0, 10)}</b></div>
                <div>Time / समय: <b>{dt.time || "—"}</b></div>
                {adm.ipd_no && <div>IPD No.: <b className="font-mono">{adm.ipd_no}</b></div>}
              </>
            }
          />
        )}

        {/* Patient identification strip — pre-filled */}
        <div className="mt-4 space-y-1.5">
          <div>
            OPD / IPD Registration No. <span className="text-ink-muted">(ओपीडी/आईपीडी पंजीकरण संख्या)</span>:{" "}
            <Filled value={`${patient.uhid || ""}${adm.ipd_no ? ` · ${adm.ipd_no}` : ""}`} cap={60} />
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-1">
            <div>Name <span className="text-ink-muted">(नाम)</span>: <Filled value={patient.name} cap={40} /></div>
            <div>Sex <span className="text-ink-muted">(लिंग)</span>: <Filled value={patient.gender} cap={10} /></div>
            <div>Age <span className="text-ink-muted">(उम्र)</span>: <Filled value={patient.age || (patient.dob ? patient.dob : "")} cap={6} /></div>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-1">
            <div>S/o, D/o, W/o <span className="text-ink-muted">(पुत्र/श्री, पुत्री/श्री, पत्नी/श्री)</span>: <Filled value={guardian} cap={40} /></div>
            <div>R/o <span className="text-ink-muted">(पता)</span>: <Filled value={address} cap={45} /></div>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-1">
            <div>Ward / Bed: <Filled value={`${adm.ward || ""}${adm.bed_no ? ` · ${adm.bed_no}` : ""}`} cap={30} /></div>
            <div>Payer: <Filled value={adm.panel_name || adm.payer_category || ""} cap={30} /></div>
            {adm.policy_no && <div>Policy No.: <Filled value={adm.policy_no} cap={20} /></div>}
          </div>
        </div>

        {/* Title — bilingual */}
        <div className="mt-5 text-center">
          <div className="text-[15px] font-bold underline">CONSENT FOR ADMISSION & TREATMENT</div>
          <div className="text-[13.5px] font-semibold underline">प्रवेश एवं उपचार के लिए सहमति</div>
        </div>

        {/* Body paragraph 1 — pre-fills doctor */}
        <p className="mt-4 text-justify">
          I hereby give my consent for my / my relative for admission &amp; treatment in this hospital under care of Dr.{" "}
          <Filled value={doctorName} cap={36} />{" "}
          and I am ready for getting investigations, admission of medicines, Injections, I/V Fluids (Glucose),
          Blood &amp; Blood products, nutritional interventions and minor procedures etc. as prescribed by clinician I/C.
          <span className="block mt-1">
            (मैं मेरे / मेरे रिश्तेदार के इस अस्पताल में प्रवेश और उपचार के लिए इस चिकित्सक{" "}
            <Filled value={doctorName} cap={36} />{" "}
            की देखरेख में अपनी सहमति देता हूँ और मैं जाँच, दवाओं के प्रशासन को प्राप्त करने के लिए, इंजेक्शन, I/V तरल पदार्थ (ग्लूकोज), रक्त और रक्त उत्पादों, पोषण हस्तक्षेप और लघु प्रक्रियाओं आदि चिकित्सक प्रभारी द्वारा निर्धारित प्रक्रियाओं के लिए तैयार हूँ।)
          </span>
        </p>

        {/* Body paragraph 2 */}
        <p className="mt-3 text-justify">
          I also give consent to make timely payments of all dues of the hospital that are incurred from time to time during the management of patient in the hospital.
          <span className="block mt-1">
            (मैं अस्पताल में मरीज के प्रबंधन के दौरान समय-समय पर खर्च, अस्पताल के सभी बकाया राशि का समय पर भुगतान करने के लिए भी सहमति देता / देती हूँ।)
          </span>
        </p>

        {/* Signature block 1: Patient / Parent / Guardian */}
        <div className="mt-5">
          <div className="font-semibold">
            Name &amp; Signature of the patient / Parent / Guardian or thumb Impression
          </div>
          <div className="text-[11.5px] text-ink-muted">रोगी / पिता / अभिभावक का नाम व हस्ताक्षर / अंगूठे का निशान</div>
          <div className="mt-2">
            Name <span className="text-ink-muted">(नाम)</span>: <Filled value={patient.name} cap={45} />
            <span className="ml-6">(Sign Here, हस्ताक्षर यहाँ करें ) {dot(28)}</span>
          </div>
        </div>

        {/* Signature block 2: Witness */}
        <div className="mt-5">
          <div className="font-semibold">
            Name &amp; Signature of the witness <span className="font-normal text-ink-muted">(गवाह का नाम व हस्ताक्षर)</span>
          </div>
          <div className="mt-2">Name <span className="text-ink-muted">(नाम)</span>: {dot(45)} <span className="ml-6">(Sign Here, हस्ताक्षर यहाँ करें ) {dot(20)}</span></div>
          <div className="mt-1">
            Relationship with Patient <span className="text-ink-muted">(रोगी के साथ संबंध)</span>: {dot(30)}
            <span className="ml-6">Date <span className="text-ink-muted">(दिनांक)</span>: <Filled value={dt.date || new Date().toISOString().slice(0, 10)} cap={14} /></span>
          </div>
        </div>

        {/* Confirmation paragraph */}
        <p className="mt-5 font-semibold">
          I CONFIRM THAT I HAVE EXPLAINED THE SCOPE OF THE ABOVE CONSENT TO THE PATIENT AND / OR FAMILY MEMBERS
        </p>
        <p className="text-[11.5px] text-ink-muted">
          मैं पुष्टि करता / करती हूँ कि मैंने रोगी और / या परिवार के सदस्यों को उपरोक्त सहमति के दायरे को समझा दिया है।
        </p>

        {/* Signature block 3: Person taking consent */}
        <div className="mt-4">
          <div className="font-semibold">
            Name &amp; Signature of the person taking the consent (Registration clerk / Nurse / Doctor)
          </div>
          <div className="text-[11.5px] text-ink-muted">सहमति लेने वाले व्यक्ति का नाम व हस्ताक्षर (पंजीकरण क्लर्क / नर्स / डॉक्टर)</div>
          <div className="mt-2">Name <span className="text-ink-muted">(नाम)</span>: <Filled value={doctorName || nameOf(adm.created_by, adm.created_by) || ""} cap={45} /> <span className="ml-6">(Sign Here, हस्ताक्षर यहाँ करें ) {dot(20)}</span></div>
          <div className="mt-1">
            Designation <span className="text-ink-muted">(पदनाम)</span>: {dot(36)}
            <span className="ml-6">Date <span className="text-ink-muted">(दिनांक)</span>: <Filled value={dt.date || new Date().toISOString().slice(0, 10)} cap={14} /></span>
          </div>
        </div>

        {/* Document footer */}
        <div className="mt-6 border-t border-border pt-2 text-[10px] text-ink-muted flex justify-between">
          <div>{hospital?.name || BRAND.name} · {branch?.name || ""} · Admission Consent · Version 1.0</div>
          <div>Generated: {new Date().toISOString().slice(0, 16).replace("T", " ")}</div>
        </div>
        <div className="mt-1 text-center text-[9px] text-ink-muted">{BRAND.footer}</div>

        {/* Floating reprint button (screen only) */}
        <div className="no-print fixed bottom-6 right-6">
          <button
            onClick={() => window.print()}
            className="rounded-md bg-brand px-4 py-2 text-white shadow-lg hover:bg-brand-700"
            data-testid="consent-print-btn"
          >
            Print / Save as PDF
          </button>
        </div>
      </div>
    </>
  );
}
